import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Download, Filter, Calendar, Edit, CheckCircle, XCircle } from "lucide-react";
import { supabase } from "../../../../src/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import * as XLSX from 'xlsx';

interface TestimoniesManagerProps {
  userRegion?: string;
  isGlobalAdmin: boolean;
}

interface Testimony {
  id: string;
  title: string;
  content: string;
  author_name: string;
  author_email: string;
  is_anonymous: boolean;
  status: string;
  admin_notes: string | null;
  created_at: string;
  region_id: string;
  regions: { name: string } | null;
}

export const TestimoniesManager: React.FC<TestimoniesManagerProps> = ({
  userRegion,
  isGlobalAdmin,
}) => {
  const [testimonies, setTestimonies] = useState<Testimony[]>([]);
  const [regions, setRegions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingTestimony, setEditingTestimony] = useState<Testimony | null>(null);
  const [editForm, setEditForm] = useState({ title: '', content: '', status: '', admin_notes: '' });
  const [filters, setFilters] = useState({
    region: "all",
    startDate: "",
    endDate: "",
    status: "all",
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchRegions();
    fetchTestimonies();
  }, [filters]);

  const fetchRegions = async () => {
    const { data, error } = await supabase
      .from('regions')
      .select('id, name')
      .order('name');
    
    if (error) {
      console.error('Error fetching regions:', error);
    } else {
      setRegions(data || []);
    }
  };

  const fetchTestimonies = async () => {
    setLoading(true);
    
    let query = supabase
      .from('testimonies')
      .select(`
        *,
        regions (name)
      `)
      .order('created_at', { ascending: false });

    // Apply filters
    if (filters.region !== "all") {
      query = query.eq('region_id', filters.region);
    }

    if (filters.startDate) {
      const startDateTime = `${filters.startDate}T00:00:00.000Z`;
      query = query.gte('created_at', startDateTime);
    }

    if (filters.endDate) {
      const endDateTime = `${filters.endDate}T23:59:59.999Z`;
      query = query.lte('created_at', endDateTime);
    }

    if (filters.status !== "all") {
      query = query.eq('status', filters.status);
    }

    // Regional admins can only see their region's testimonies
    if (!isGlobalAdmin && userRegion) {
      query = query.eq('region_id', userRegion);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching testimonies:', error);
      toast({
        title: "Error",
        description: "Failed to fetch testimonies",
        variant: "destructive",
      });
    } else {
      setTestimonies(data || []);
    }
    
    setLoading(false);
  };

  const exportToExcel = () => {
    const exportData = testimonies.map(testimony => ({
      'Title': testimony.title,
      'Author': testimony.is_anonymous ? 'Anonymous' : testimony.author_name,
      'Email': testimony.is_anonymous ? 'Hidden' : testimony.author_email,
      'Region': testimony.regions?.name || 'N/A',
      'Status': testimony.status,
      'Content': testimony.content,
      'Submitted Date': new Date(testimony.created_at).toLocaleDateString(),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Testimonies');
    
    const fileName = `testimonies_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    toast({
      title: "Success",
      description: "Testimonies exported to Excel successfully",
    });
  };

  const handleEdit = (testimony: Testimony) => {
    setEditingTestimony(testimony);
    setEditForm({
      title: testimony.title,
      content: testimony.content,
      status: testimony.status,
      admin_notes: testimony.admin_notes || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateTestimony = async () => {
    if (!editingTestimony) return;

    try {
      const { error } = await supabase
        .from('testimonies')
        .update({
          title: editForm.title,
          content: editForm.content,
          status: editForm.status,
          admin_notes: editForm.admin_notes
        })
        .eq('id', editingTestimony.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Testimony updated successfully",
      });

      setEditDialogOpen(false);
      fetchTestimonies();
    } catch (error) {
      console.error('Error updating testimony:', error);
      toast({
        title: "Error",
        description: "Failed to update testimony",
        variant: "destructive",
      });
    }
  };

  const handleStatusChange = async (testimony: Testimony, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('testimonies')
        .update({ status: newStatus })
        .eq('id', testimony.id);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Testimony ${newStatus}`,
      });

      fetchTestimonies();
    } catch (error) {
      console.error('Error updating status:', error);
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'default';
      case 'pending': return 'secondary';
      case 'rejected': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
          <Button onClick={exportToExcel} disabled={testimonies.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Export to Excel
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={filters.status}
                onValueChange={(value) => setFilters({ ...filters, status: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Region</Label>
              <Select
                value={filters.region}
                onValueChange={(value) => setFilters({ ...filters, region: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Regions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Regions</SelectItem>
                  {regions.map((region) => (
                    <SelectItem key={region.id} value={region.id}>
                      {region.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Date</Label>
              <Input
                type="date"
                value={filters.startDate}
                onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>End Date</Label>
              <Input
                type="date"
                value={filters.endDate}
                onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Testimonies ({testimonies.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading testimonies...</div>
          ) : testimonies.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No testimonies found matching the current filters.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Author</TableHead>
                    <TableHead>Region</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Submitted</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {testimonies.map((testimony) => (
                    <TableRow key={testimony.id}>
                      <TableCell className="font-medium max-w-xs truncate">{testimony.title}</TableCell>
                      <TableCell>
                        {testimony.is_anonymous ? 'Anonymous' : testimony.author_name}
                      </TableCell>
                      <TableCell>{testimony.regions?.name || 'N/A'}</TableCell>
                      <TableCell>
                        <Badge variant={getStatusColor(testimony.status)}>
                          {testimony.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {new Date(testimony.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(testimony)}
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {testimony.status !== 'approved' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(testimony, 'approved')}
                              title="Approve"
                            >
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            </Button>
                          )}
                          {testimony.status !== 'rejected' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleStatusChange(testimony, 'rejected')}
                              title="Reject"
                            >
                              <XCircle className="h-4 w-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Testimony</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title</Label>
              <Input
                value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              />
            </div>
            <div>
              <Label>Content</Label>
              <Textarea
                value={editForm.content}
                onChange={(e) => setEditForm({ ...editForm, content: e.target.value })}
                rows={8}
              />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Admin Notes</Label>
              <Textarea
                value={editForm.admin_notes}
                onChange={(e) => setEditForm({ ...editForm, admin_notes: e.target.value })}
                rows={3}
                placeholder="Add notes for internal use..."
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateTestimony}>
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};